import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import apiClient from '../../api/apiClient';
import { toastError } from '../common/toast';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import UpdateComplaintStatusModal from './UpdateComplaintStatusModal';
import tenantStorage from '../../utils/tenantStorage';

const AssignedComplaints = ({ loadData = false }) => {
  const navigate = useNavigate();
  
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [updateAction, setUpdateAction] = useState(null);

  useEffect(() => {
    if (loadData) {
      fetchAssignedComplaints();
    }
  }, [loadData]);

  const fetchAssignedComplaints = async () => {
    setLoading(true);
    try {
      // Check if user is staff to add mode=staff parameter
      const userRole = tenantStorage.getItem('userRole');
      const isStaff = userRole === 'RT_Staff';
      const params = new URLSearchParams();
      
      if (isStaff) {
        params.append('mode', 'staff');
      }
      
      const url = params.toString() ? `/complaints/assigned?${params.toString()}` : '/complaints/assigned';
      const response = await apiClient.get(url);
      setComplaints(response.data || []);
    } catch (error) {
      console.error('Error fetching assigned complaints:', error);
      toastError('Failed to fetch assigned complaints');
      setComplaints([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClass = status.toLowerCase().replace(/_/g, '-');
    return (
      <span className={`complaint-status status-${statusClass}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    return (
      <span className={`complaint-priority priority-${priority.toLowerCase()}`}>
        {priority}
      </span>
    );
  };

  const handleActionClick = (complaint, action) => {
    setSelectedComplaint(complaint);
    setUpdateAction(action);
    setShowUpdateModal(true);
  };

  const getActionButton = (complaint) => {
    const status = complaint.status;
    
    if (status === 'open' || status === 'assigned') {
      return (
        <RTButton
          type="link"
          onClick={() => handleActionClick(complaint, 'start')}
        >
          Start Ticket
        </RTButton>
      );
    } else if (status === 'in_progress') {
      return (
        <RTButton
          type="link"
          onClick={() => handleActionClick(complaint, 'resolve')}
        >
          Resolve Ticket
        </RTButton>
      );
    } else if (status === 'resolved' && userRole !== 'RT_Staff') {
      return (
        <RTButton
          type="link"
          onClick={() => handleActionClick(complaint, 'close')}
        >
          Close Ticket
        </RTButton>
      );
    }
    return null;
  };

  // Get user role from localStorage
  const userRole = tenantStorage.getItem('userRole');

  const columns = [
    {
      Header: 'Complaint ID',
      accessor: 'complaint_id',
      Cell: ({ value }) => `#${value}`
    },
    {
      Header: 'Date',
      accessor: 'created_at',
      Cell: ({ value }) => formatDateToDDMMYYYY(value)
    },
    {
      Header: 'PG Name',
      accessor: 'pg_name',
      Cell: ({ value, row }) => {
        if (userRole === 'RT_Staff') {
          return value;
        }
        return (
          <RTButton
            type="link"
            onClick={() => window.open(`/paying-guest/view/${row.pg_id}`, '_blank')}
            className="rt-link-button"
          >
            {value}
          </RTButton>
        );
      }
    },
    {
      Header: 'Building',
      accessor: 'building_name',
      Cell: ({ value, row }) => {
        if (userRole === 'RT_Staff') {
          return value;
        }
        return (
          <RTButton
            type="link"
            onClick={() => window.open(`/building/view/${row.building_id}`, '_blank')}
            className="rt-link-button"
          >
            {value}
          </RTButton>
        );
      }
    },
    {
      Header: 'Type',
      accessor: 'complaint_type',
      Cell: ({ value }) => {
        // Handle both string and object types
        if (typeof value === 'object' && value !== null) {
          return value.type_name || value.name || 'Unknown';
        }
        return value || 'Unknown';
      }
    },
    {
      Header: 'Priority',
      accessor: 'priority',
      Cell: ({ value }) => getPriorityBadge(value)
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }) => getStatusBadge(value)
    },
    {
      Header: 'Assigned To',
      accessor: 'assigned_to_name',
      Cell: ({ value }) => value || 'Unknown'
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          {getActionButton(row)}
          <RTButton
            type="link"
            onClick={() => navigate(`/complaints/view/${row.complaint_id}`)}
            style={{ fontWeight: 'bold' }}
          >
            View
          </RTButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <RTSpinner size="medium" showMessage={true} message="Loading assigned complaints..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 0' }}>
      {!loadData ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#666',
          fontSize: '16px'
        }}>
          Click on this tab to load assigned complaints
        </div>
      ) : (
        <Table
          columns={columns}
          data={complaints}
          loading={loading}
          exportable={true}
          showSearch={true}
          showPagination={true}
          showTotalCount={true}
          totalCountText="Total Assigned Complaints:"
        />
      )}

      {/* Update Complaint Status Modal */}
      <UpdateComplaintStatusModal
        isOpen={showUpdateModal}
        onClose={() => {
          setShowUpdateModal(false);
          setSelectedComplaint(null);
          setUpdateAction(null);
        }}
        complaint={selectedComplaint}
        action={updateAction}
        onSuccess={() => {
          fetchAssignedComplaints(); // Refresh the list
        }}
      />
    </div>
  );
};

export default AssignedComplaints;
